import { Trans, useLingui } from '@lingui/react/macro';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { CheckmarkCircle } from '@remarkable/ark-icons';
import { Typography } from '@remarkable/ark-web';
import { useEffect } from 'react';

import { ButtonClicked, ComponentRendered } from 'src/ampli';
import { tracker } from 'src/analytics/tracker';
import { ComponentLocations } from 'src/analytics/trackingTypes';
import type {
  CloudConnectSubscription,
  Plan,
  Price,
} from 'src/api/endpoints/cloudConnect.types';
import {
  usePricePreview,
  useUpdateCloudConnectBillingInterval,
} from 'src/api/queries';
import { Button, Modal, NotificationBox, Spinner } from 'src/components';
import { LinkExternal } from 'src/components/LinkExternal';
import { formatDate } from 'src/utils/formatDate';
import { formatAmount } from 'src/utils/productUtils';
import { SUPPORT_URL } from 'src/utils/urls/supportUrls';

import { useOfferExpirationDetails } from '../utils/getOfferExpirationDetails';

export const CloudConnectChangePaymentIntervalToAnnualForm = ({
  annualPrice,
  currentPlan,
  subscription,
}: {
  annualPrice: Price;
  currentPlan: Plan;
  subscription: CloudConnectSubscription;
}) => {
  const { t, i18n } = useLingui();
  const updatePaymentInterval = useUpdateCloudConnectBillingInterval();
  const pricePreview = usePricePreview('annual');
  const nextBillingDate = formatDate(subscription.periodEnd, {
    fallback: t`No invoice available`,
    locale: i18n.locale,
  });
  const confirmText = t`Switch to annual billing`;
  const { offerHasEnded, daysUntilOfferEnds, offerEndDate } =
    useOfferExpirationDetails();
  const offerEndDateFormatted = formatDate(offerEndDate, {
    locale: i18n.locale,
  });
  const pricePreviewPrice = pricePreview.data ?? null;
  const effectiveAnnualPrice = pricePreviewPrice ?? annualPrice;
  const effectiveAnnualPriceText = formatAmount(
    effectiveAnnualPrice.totalAmount,
    effectiveAnnualPrice.currency,
    i18n.locale
  );
  const campaignIsActive = !offerHasEnded;
  const invoicePrice = subscription.nextInvoice?.amountDue;

  useEffect(() => {
    tracker.trackEvent(
      new ComponentRendered({
        component_name: 'CloudConnectChangePaymentIntervalToAnnualForm',
        component_type: 'change payment interval form',
      })
    );
  }, []);

  if (!invoicePrice) {
    return (
      <NotificationBox variant="error" title={t`Something went wrong`}>
        <Trans>
          No payment or invoice information available. Please try again later or
          contact support if the problem persists.
        </Trans>
      </NotificationBox>
    );
  }

  const currentMonthlyPrice = formatAmount(
    invoicePrice.totalAmount,
    invoicePrice.currency,
    i18n.locale
  );
  const currentPricePerYear = formatAmount(
    invoicePrice.totalAmount * 12,
    invoicePrice.currency,
    i18n.locale
  );

  if (updatePaymentInterval.data?.status === 'success') {
    const currentPrice = formatAmount(
      invoicePrice.totalAmount,
      invoicePrice.currency,
      i18n.locale
    );

    return (
      <>
        <CheckmarkCircle
          data-cy="payment-interval-confirmation-icon"
          size={86}
          className="mx-auto mb-24 text-feedback-green-500"
        />
        <Modal.Title asChild>
          <Typography as="h2" variant="heading-xs">
            <Trans>
              You&apos;ve switched to{' '}
              <span className="whitespace-nowrap">annual billing</span>
            </Trans>
          </Typography>
        </Modal.Title>

        <Modal.Description asChild>
          <Typography as="p" variant="body-md" className="mt-16">
            <Trans>
              You&apos;ll be charged {currentPrice} on{' '}
              <span className="font-bold whitespace-nowrap">
                {nextBillingDate}
              </span>
            </Trans>
          </Typography>
        </Modal.Description>

        <Modal.Close asChild>
          <Button
            className="mx-auto mt-24 w-full xs:w-auto"
            data-cy="close-payment-interval-confirmation-button"
          >
            <Trans>Close</Trans>
          </Button>
        </Modal.Close>
      </>
    );
  }

  if (currentPlan.interval === 'annual') {
    return (
      <div className="flex flex-col gap-24">
        <Modal.Title asChild>
          <Typography as="h2" variant="heading-sm">
            <Trans>Already on annual billing</Trans>
          </Typography>
        </Modal.Title>

        <Modal.Description asChild>
          <Typography as="p" variant="body-md">
            <Trans>You are currently on the annual billing plan.</Trans>
          </Typography>
        </Modal.Description>

        <div className="flex w-full justify-center gap-8">
          <Modal.Close asChild>
            <Button variant="secondary">
              <Trans>Close</Trans>
            </Button>
          </Modal.Close>
        </div>
      </div>
    );
  }

  const onSubmit = () => {
    tracker.trackEvent(
      new ButtonClicked({
        component_location:
          ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL,
        text: confirmText,
        action: 'confirm change payment interval',
      })
    );

    updatePaymentInterval.mutate('annual');
  };

  let annualPlanDetails;
  if (campaignIsActive) {
    if (pricePreview.isPending) {
      return (
        <>
          <AnnualModalTitle />
          <Spinner />
          <VisuallyHidden>
            <Modal.Description>
              <Trans>Loading billing information...</Trans>
            </Modal.Description>
          </VisuallyHidden>
        </>
      );
    }

    if (pricePreview.isError) {
      return (
        <>
          <AnnualModalTitle />
          <NotificationBox
            error={pricePreview.error}
            className="mt-16"
            title={t`Something went wrong`}
          >
            <Trans>
              Please try again. If the problem keeps happening,&nbsp;
              <LinkExternal
                inline
                to={SUPPORT_URL.CONTACT_SUPPORT}
                componentLocation={
                  ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
                }
              >
                contact Support
              </LinkExternal>
              .
            </Trans>
          </NotificationBox>

          <div className="mt-24 flex w-full justify-center">
            <Modal.Close asChild>
              <Button type="button" variant="secondary">
                <Trans>Close</Trans>
              </Button>
            </Modal.Close>
          </div>
        </>
      );
    }

    if (!pricePreviewPrice) {
      return null;
    }

    annualPlanDetails = (
      <AnnualCampaignDetails
        daysUntilOfferEnds={daysUntilOfferEnds}
        optionPrice={effectiveAnnualPriceText}
        annualPrice={formatAmount(
          annualPrice.totalAmount,
          annualPrice.currency,
          i18n.locale
        )}
        offerEndDateFormatted={offerEndDateFormatted}
      />
    );
  } else {
    annualPlanDetails = (
      <StandardAnnualDetails
        optionPrice={effectiveAnnualPriceText}
        currentPricePerYear={currentPricePerYear}
        currentPrice={currentMonthlyPrice}
        nextBillingDate={nextBillingDate}
      />
    );
  }

  return (
    <>
      <AnnualModalTitle />

      {annualPlanDetails}

      {updatePaymentInterval.isError && (
        <NotificationBox
          error={updatePaymentInterval.error}
          className="mt-16"
          title={t`Something went wrong`}
          data-cy="error-message"
        >
          <span className="mt-4">
            <Trans>
              Please try again. If the problem keeps happening,&nbsp;
              <LinkExternal
                inline
                to={SUPPORT_URL.CONTACT_SUPPORT}
                componentLocation={
                  ComponentLocations.MANAGE_CONNECT.PAYMENT_INTERVAL_MODAL
                }
              >
                contact Support
              </LinkExternal>
              .
            </Trans>
          </span>
        </NotificationBox>
      )}

      <div className="mt-24 flex w-full flex-col flex-wrap justify-center gap-16 xs:flex-row">
        <Modal.Close asChild>
          <Button
            type="button"
            variant="secondary"
            className="w-full xs:w-fit"
            disabled={updatePaymentInterval.isPending}
            data-cy="cancel-change-payment-interval-button"
          >
            <Trans>Cancel</Trans>
          </Button>
        </Modal.Close>

        <Button
          onClick={onSubmit}
          loading={updatePaymentInterval.isPending}
          className="w-full xs:w-fit"
          data-cy="confirm-change-payment-interval-button"
          variant="primary"
        >
          {confirmText}
        </Button>
      </div>
    </>
  );
};

function AnnualModalTitle() {
  return (
    <Modal.Title asChild>
      <Typography as="h2" variant="heading-sm">
        <Trans>Switch to annual billing</Trans>
      </Typography>
    </Modal.Title>
  );
}

function AnnualCampaignDetails({
  daysUntilOfferEnds,
  optionPrice,
  annualPrice,
  offerEndDateFormatted,
}: {
  daysUntilOfferEnds: number;
  optionPrice: string;
  annualPrice: string;
  offerEndDateFormatted: string;
}) {
  return (
    <>
      <div className="my-24">
        <Modal.Description asChild>
          <Typography as="p" variant="body-md">
            <Trans>
              For the next {daysUntilOfferEnds} days, you can get annual Connect
              for just <strong>{optionPrice}</strong>.
              <br />
              That&apos;s 3 months free.
            </Trans>
          </Typography>
        </Modal.Description>
      </div>

      <Typography as="p" variant="body-md" className="mb-32">
        <Trans>Your annual plan begins on your next billing date.</Trans>
        <br />
        <Trans>
          Automatically renews at <strong>{annualPrice}</strong> every year.
        </Trans>
      </Typography>

      <Typography as="p" variant="body-md" className="mb-32">
        <Trans>
          Offer ends{' '}
          <strong data-cy="change-payment-next-billing-date">
            {offerEndDateFormatted}
          </strong>
        </Trans>
      </Typography>
    </>
  );
}

function StandardAnnualDetails({
  optionPrice,
  currentPricePerYear,
  currentPrice,
  nextBillingDate,
}: {
  optionPrice: string;
  currentPricePerYear: string;
  currentPrice: string;
  nextBillingDate: string;
}) {
  return (
    <>
      <div className="my-24">
        <Modal.Description asChild>
          <Typography as="p" variant="body-md">
            <Trans comment="Payment interval change confirmation message. Compares the price of the new annual plan to the current monthly plan (in both yearly and monthly terms).">
              Pay <strong>{optionPrice}/year</strong> instead of
              <br />
              {currentPricePerYear}/year{' '}
              <span className="whitespace-nowrap">({currentPrice}/month)</span>
            </Trans>
          </Typography>
        </Modal.Description>
      </div>

      <Typography as="p" variant="body-md" className="mb-32">
        <Trans>
          Your new plan starts{' '}
          <strong data-cy="change-payment-next-billing-date">
            {nextBillingDate}
          </strong>
        </Trans>
      </Typography>
    </>
  );
}
